<template>
	<form
		name="sell-inquiry-form"
		class="sellInquiryForm"
		noValidate
		@submit.prevent="submit()"
	>
		<div v-if="isSaving || isLoading" class="form-content">
			<div class="form-container marginLargeBottom loading-wrapper">
				<div class="loading-container">
					<div>
						<i class="fa fa-spinner fa-spin fa-2x fa-fw" />
						<span class="sr-only">Loading...</span>
					</div>
				</div>
			</div>
		</div>
		<div v-show="!isSaving && !isLoading" class="form-content">
			<div class="form-container marginLargeBottom">
				<h4 class="marginNormalBottom">Selling Inquiry</h4>
				<p>
					Thank you for your interest in consigning with Leland Little. Please
					submit the below form and our team will review your object(s) for
					potential consignment. Combine all categories into one submission,
					when possible.
				</p>
				<div class="row row-header">
					<div>
						<div class="header">Contact Information</div>
					</div>
				</div>
				<div class="row">
					<div class="row row-text">
						<div>
							<div class="text">
								Full name <span class="required-field">*</span>
							</div>
							<div class="input-field">
								<input
									v-model="formData.name"
									required="required"
									name="name"
									type="text"
									:class="getField('name').valid ? 'valid' : ''"
								/>
								<div v-if="getField('name').error" id="err-text-1">
									<div class="error" v-html="getField('name').errorMessage" />
								</div>
							</div>
						</div>
					</div>
					<div class="row row-text">
						<div>
							<div class="text">
								Phone Number <span class="required-field">*</span>
							</div>
							<div class="input-field">
								<input
									v-model="formData.phone"
									required="required"
									name="phone"
									type="text"
									:class="getField('phone').valid ? 'valid' : ''"
								/>
								<div v-if="getField('phone').error" id="err-text-1">
									<div class="error" v-html="getField('phone').errorMessage" />
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="row">
					<div class="row row-text">
						<div>
							<div class="text">
								Email Address <span class="required-field">*</span>
							</div>
							<div class="input-field">
								<input
									v-model="formData.email"
									required="required"
									name="email"
									type="text"
									:class="getField('email').valid ? 'valid' : ''"
								/>
								<div v-if="getField('email').error" id="err-text-1">
									<div class="error" v-html="getField('email').errorMessage" />
								</div>
							</div>
						</div>
					</div>
					<div class="row row-text">
						<div>
							<div class="text">
								Location (City/State) <span class="required-field">*</span>
							</div>
							<div class="input-field">
								<input
									v-model="formData.location"
									required="required"
									name="location"
									type="text"
									:class="getField('location').valid ? 'valid' : ''"
								/>
								<div v-if="getField('location').error" id="err-text-1">
									<div
										class="error"
										v-html="getField('location').errorMessage"
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="row row-header">
					<div>
						<div class="header">Property Information</div>
					</div>
				</div>
				<div class="row row-textarea">
					<div>
						<div class="textarea">
							Brief Description of item(s) or estate
							<span class="required-field">*</span>
						</div>
						<div class="input-field">
							<textarea
								v-model="formData.description"
								class="materialize-textarea"
								required="required"
								data-error="#err-textarea-6"
								name="description"
							/>
							<div v-if="getField('description').error" id="err-text-1">
								<div
									class="error"
									v-html="getField('description').errorMessage"
								/>
							</div>
						</div>
					</div>
				</div>
				<div class="row row-textarea">
					<div>
						<div class="textarea">
							Additional Information (artist or maker, period and/or style,
							number of items in the service or set)
						</div>
						<div class="input-field">
							<textarea
								v-model="formData.extra_information"
								class="materialize-textarea"
								data-error="#err-textarea-6"
								name="extra_information"
							/>
							<div v-if="getField('extra_information').error" id="err-text-1">
								<div
									class="error"
									v-html="getField('extra_information').errorMessage"
								/>
							</div>
						</div>
					</div>
				</div>
				<div class="row row-textarea">
					<div>
						<div class="textarea">Provenance (chronology of ownership)</div>
						<div class="input-field">
							<textarea
								v-model="formData.provenance"
								class="materialize-textarea"
								data-error="#err-textarea-6"
								name="provenance"
							/>
							<div v-if="getField('provenance').error" id="err-text-1">
								<div
									class="error"
									v-html="getField('provenance').errorMessage"
								/>
							</div>
						</div>
					</div>
				</div>
				<div class="row row-header">
					<div>
						<div class="header">Upload Images</div>
					</div>
				</div>
				<div class="row row-file">
					<div>
						<div class="helper">
							Images are helpful in our review process. Room or group snapshots
							are acceptable to illustrate the extent of a collection.
						</div>
						<div class="input-field">
							<div class="file-field input-field">
								<div class="btn file-upload-btn marginSmallRight">
									<img
										:src="require(`@/assets/icons/folder-1.svg`)"
										alt="Select files"
									/>
									<input
										type="file"
										name="files"
										title="Please select a file"
										multiple="true"
										accept=".pdf,.doc,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.heic,.mp4,.mov"
										@input="selectFiles"
									/>
								</div>
								<div
									v-if="isMobile && !cameraError"
									class="btn camera-upload-btn"
								>
									<img
										:src="require(`@/assets/icons/camera-1.svg`)"
										alt="Open Camera"
									/>
									<input
										type="file"
										name="photos"
										accept="image/*"
										multiple="true"
										capture="camera"
										@input="selectFiles"
									/>
								</div>
								<div class="file-path-wrapper">
									<p
										class="marginNoneBottom"
										v-html="
											isMobile
												? 'Browse and upload using the file icon or take images using the camera icon.'
												: 'Browse and upload using the file icon.'
										"
									/>
								</div>
								<!-- Display selected image thumbnails -->
								<div v-if="formData.files.length > 0" class="file-list">
									<div
										v-for="file in pendingFiles"
										v-if="pendingFiles.length > 0"
										:key="file.name"
										class="thumbnail-wrapper"
									>
										<div class="loading-container">
											<div>
												<i class="fa fa-spinner fa-spin fa-2x fa-fw" />
												<span class="sr-only">Loading...</span>
											</div>
										</div>
									</div>
									<div
										v-for="(file, index) in formData.files"
										:key="file.name"
										class="thumbnail-wrapper"
									>
										<img
											:src="file.thumbnailUrl"
											:alt="file.name"
											class="thumbnail"
											:title="file.name"
											@error="thumbnailError"
										/>
										<button class="remove-file-btn" @click="removeFile(index)">
											<img :src="require(`@/assets/icons/close-x-1.svg`)" />
										</button>
									</div>
								</div>
							</div>
						</div>
						<div v-if="getField('files').error" id="err-text-1">
							<div class="error" v-html="getField('files').errorMessage" />
						</div>
					</div>
				</div>
				<div class="row row-header">
					<div>
						<div class="header">Additional Information</div>
					</div>
				</div>
				<div class="row" />
				<div class="row row-text paddingNoneLeft">
					<div class="col s12">
						<div class="text">
							If you have been in contact with a Leland Little team member, whom
							did you speak with? <span class="required-field">*</span>
						</div>
						<div class="input-field">
							<input
								v-model="formData.contact"
								required="required"
								data-error="#err-text-10"
								name="contact"
								type="text"
								:class="getField('contact').valid ? 'valid' : ''"
							/>
							<div v-if="getField('contact').error" id="err-text-1">
								<div class="error" v-html="getField('contact').errorMessage" />
							</div>
						</div>
					</div>
				</div>
				<div class="row row-header" />
				<div class="row row-radio-group">
					<div>
						<div class="radio-group marginNormalBottom">
							Is this time sensitive for you?
						</div>
						<div class="new-input-field">
							<p>
								<label>
									<input
										id="ctrlq-radio-group-11-0"
										class="with-gap garlic-auto-save"
										data-error="#err-radio-group-11"
										name="time_sensitive"
										type="radio"
										value="1"
										@change="radioUpdate"
									/>
									<span> Yes </span>
								</label>
							</p>
							<p>
								<label>
									<input
										id="ctrlq-radio-group-11-1"
										class="with-gap garlic-auto-save"
										data-error="#err-radio-group-11"
										name="time_sensitive"
										type="radio"
										value="0"
										@change="radioUpdate"
									/>
									<span> No </span>
								</label>
							</p>
							<br />
							<div v-if="getField('time_sensitive').error" id="err-text-1">
								<div
									class="error"
									v-html="getField('time_sensitive').errorMessage"
								/>
							</div>
						</div>
					</div>
				</div>
				<div class="row submit">
					<div class="input-field col m6 s6">
						<button
							type="submit"
							class="waves-effect waves-light btn btn-large btn-submit"
						>
							Submit
						</button>
					</div>
					<div class="input-field col m6 s6">
						<div class="preloader-wrapper big active spinner hide">
							<div class="spinner-layer forms-studio-spinner">
								<div class="circle-clipper left">
									<div class="circle" />
								</div>
								<div class="gap-patch">
									<div class="circle" />
								</div>
								<div class="circle-clipper right">
									<div class="circle" />
								</div>
							</div>
						</div>
					</div>
				</div>
				<div v-if="formValid === false" class="row">
					<div class="col s12 validationError">
						<p>The following fields are required:</p>
						<br />
						<ul>
							<li v-for="field in errorFields" :key="field.name">
								{{ field.label }}
							</li>
						</ul>
					</div>
				</div>
				<div class="row" />
			</div>
		</div>
		<AlertComponent
			v-if="showAlert"
			:title="alertTitle"
			:message="alertMessage"
			:secondary-action="null"
			@close="closeAlert()"
		/>
	</form>
</template>

<script>
import AlertComponent from "../../../components/Shared/AlertComponent.vue";
import { v4 as uuid } from "uuid";

const defaultFormDataState = {
	form_id: uuid(),
	name: "",
	phone: "",
	email: "",
	location: "",
	description: "",
	provenance: "",
	extra_information: "",
	files: [],
	contact: "",
	time_sensitive: 0,
};

export default {
	name: "SellInquiryForm",
	components: {
		AlertComponent,
	},
	data: function () {
		return {
			formData: { ...defaultFormDataState },
			formFields: [
				{
					name: "name",
					label: "Full name",
					type: "text",
					required: true,
					error: false,
					errorMessage: "",
				},
				{
					name: "phone",
					label: "Phone Number",
					type: "phone",
					required: true,
					error: false,
					errorMessage: "",
				},
				{
					name: "email",
					label: "Email Address",
					type: "email",
					required: true,
					error: false,
					errorMessage: "",
				},
				{
					name: "location",
					label: "Location (City/State)",
					type: "text",
					required: true,
					error: false,
					errorMessage: "",
				},
				{
					name: "description",
					label: "Brief Description of item(s) or estate",
					type: "text",
					required: true,
					error: false,
					errorMessage: "",
				},
				{
					name: "provenance",
					label: "Provenance (chronology of ownership)",
					type: "text",
					required: false,
					error: false,
					errorMessage: "",
				},
				{
					name: "extra_information",
					label:
						"Additional Information (artist or maker, period and/or style, number of items in the service or set)",
					type: "text",
					required: false,
					error: false,
					errorMessage: "",
				},
				{
					name: "files",
					label: "Upload Images",
					type: "array",
					required: true,
					error: false,
					errorMessage: "",
				},
				{
					name: "contact",
					label:
						"If you have been in contact with a Leland Little team member, whom did you speak with?",
					type: "text",
					required: true,
					error: false,
					errorMessage: "",
				},
				{
					name: "time_sensitive",
					label: "Is this time sensitive for you?",
					type: "text",
					required: false,
					error: false,
					errorMessage: "",
				},
			],
			formValid: true,
			hasCameraAccess: "pending",
			isMobile:
				/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
					navigator.userAgent
				),
			pendingFiles: [],
			showAlert: false,
			alertTitle: "",
			alertMessage: "",
			submitted: false,
			cameraError: null,
		};
	},
	computed: {
		sellInquiryFormData: function () {
			return this.$store.getters.sellInquiryFormData;
		},
		isLoading: function () {
			return this.$store.getters.isLoadingSellInquiryFormData;
		},
		isSaving: function () {
			return this.$store.getters.isSavingSellInquiryFormData;
		},
	},
	methods: {
		/**
		 * Check if the browser has access to the camera
		 * @param {*} e
		 */
		checkCameraAccess: async function (e) {
			e.preventDefault();
			try {
				// Try to access the camera
				const stream = await navigator.mediaDevices.getUserMedia({
					video: true,
					audio: false,
					preferCurrentTab: true,
				});
				// If successful, set hasCameraAccess to true
				this.hasCameraAccess = true;
				// Stop the stream to release the camera
				stream.getTracks().forEach((track) => track.stop());
				// Continue with the default event
				e.target.click();
			} catch (error) {
				// If there's an error (e.g., camera not available or permission denied), set hasCameraAccess to false
				console.error("Error accessing camera:", error);
				this.hasCameraAccess = false;
				this.cameraError = error;
			}
		},
		/**
		 * Close the alert
		 */
		closeAlert: function () {
			if (this.submitted) {
				// Reset the form
				this.resetForm();
				// Go to /sell/
				window.location.href = "/sell/";
				return;
			}
			this.showAlert = false;
		},
		/**
		 * Get a form field by name
		 * @param {string} name
		 */
		getField: function (name) {
			return this.formFields.find((field) => field.name === name);
		},
		/**
		 * Handle radio button updates
		 * @param {*} e
		 */
		radioUpdate: function (e) {
			const { target } = e;
			this.formData[target.name] = target.value;
		},
		/**
		 * Remove a file from the form data
		 * @param {number} index
		 */
		removeFile: async function (index) {
			const fileToDelete = this.formData.files[index];
			this.formData.files.splice(index, 1);
			// dsremove from server also
			const payload = {
				fileId: fileToDelete.remote.file.id,
				formId: this.formData.form_id,
			};
			await this.$store
				.dispatch("deleteSellInquiryFormFileData", payload)
				.catch((error) => {
					console.error(error);
				});
		},
		/**
		 * Get the thumbnail URL for a file
		 * @param {File} file
		 */
		getThumbnailUrl(file) {
			// Assuming file is an object with a 'url' property for the thumbnail
			// You might need to adjust this based on how you handle file uploads
			return URL.createObjectURL(file);
		},
		/**
		 * Handle file selection
		 * @param {*} e
		 */
		selectFiles: function (e) {
			const { target } = e;
			const { files = [] } = target;
			// Get the file data and submit it to the api.
			if (files && files.length) {
				for (const file of files) {
					// ,If file size is greater than 99MB, show an alert
					if (file.size > 104857600) {
						this.alertTitle = "Sorry";
						this.alertMessage = "File size should not exceed 100MB";
						this.showAlert = true;
						return;
					}
					this.pendingFiles.unshift(file);
					this.submitFile(file);
				}
			}
		},
		/**
		 * Submit the form data
		 */
		submit: async function () {
			const validated = this.validate();
			if (!validated) {
				return;
			}
			const payload = {
				data: this.formData,
			};
			const response = await this.$store
				.dispatch("submitSellInquiryFormData", payload)
				.catch((error) => {
					console.error(error);
				});
			if (response && response.status === true) {
				// Show success alert
				this.submitted = true;
				this.alertTitle = "Success";
				this.alertMessage = "Your inquiry has been submitted successfully";
				this.showAlert = true;
			}
		},
		/**
		 * Submit a file to the server
		 * @param {File} file
		 */
		submitFile: async function (file) {
			const payload = {
				data: file,
				formId: this.formData.form_id,
			};
			const response = await this.$store
				.dispatch("submitSellInquiryFormFileData", payload)
				.catch((error) => {
					console.error(error);
				});
			// Find it in pendingFiles and remove it
			const index = this.pendingFiles.findIndex((f) => f === file);
			if (index > -1) {
				this.pendingFiles.splice(index, 1);
			}
			if (response && response.status === true) {
				// Add the file data to the form data
				file.remote = response.data;
				file.thumbnailUrl = await this.generateThumbnail(file, [80, 80]).catch(
					(error) => {
						console.error("Error generating thumbnail:", error);
						return require("@/assets/file.jpg");
					}
				);
				this.formData.files.unshift(file);
				return;
			}
			// Show an error alert
			this.alertTitle = "Error";
			this.alertMessage = "There was an error uploading the file";
			this.showAlert = true;
		},
		deleteFile: async function (file) {
			const payload = {
				fileId: file.remote.id,
				formId: this.formData.form_id,
			};
			const response = await this.$store
				.dispatch("deleteSellInquiryFormFileData", payload)
				.catch((error) => {
					console.error(error);
				});
			if (response && response.status === true) {
				// Remove the file data from the form data
				const index = this.formData.files.findIndex((f) => f === file);
				if (index > -1) {
					this.formData.files.splice(index, 1);
				}
			}
		},
		/**
		 * Reset the form
		 */
		resetForm: function () {
			this.formData = { ...defaultFormDataState, id: uuid() };
		},
		/**
		 * Handle thumbnail errors
		 * @param {*} e
		 */
		thumbnailError: function (e) {
			e.target.src = require("@/assets/file.jpg");
		},
		/**
		 * Generate a thumbnail for an image
		 * @param {File} file
		 * @param {number[]} boundBox
		 */
		generateThumbnail: function (file, boundBox) {
			if (!boundBox || boundBox.length !== 2) {
				throw new Error("You need to give the boundBox");
			}
			const canvas = document.createElement("canvas");
			const ctx = canvas.getContext("2d");
			if (!ctx) {
				throw new Error("Context not available");
			}

			return new Promise((resolve, reject) => {
				const img = new Image();
				img.onerror = reject;
				img.onload = function () {
					const scaleRatio =
						Math.min(...boundBox) / Math.max(img.width, img.height);
					const w = img.width * scaleRatio;
					const h = img.height * scaleRatio;
					canvas.width = w;
					canvas.height = h;
					ctx.drawImage(img, 0, 0, w, h);
					return resolve(canvas.toDataURL(file.type));
				};
				img.src = window.URL.createObjectURL(file);
			});
		},
		/**
		 * Validate the form
		 */
		validate: function () {
			this.formValid = true;
			// Loop through the formData and check against the formFields
			for (const field of this.formFields) {
				if (field.required && !this.formData[field.name]) {
					this.formValid = false;
					field.error = true;
					field.errorMessage = "This is a required question";
					field.valid = false;
					continue;
				}
				if (
					field.type === "email" &&
					!this.validateEmail(this.formData[field.name])
				) {
					this.formValid = false;
					field.error = true;
					field.errorMessage = "Please enter a valid email address";
					field.valid = false;
					continue;
				}
				if (field.type === "array" && !this.formData[field.name].length) {
					this.formValid = false;
					field.error = true;
					field.errorMessage = "Please upload at least one image";
					field.valid = false;
					continue;
				}
				if (
					field.type === "phone" &&
					!this.formData[field.name].match(
						/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/
					)
				) {
					this.formValid = false;
					field.error = true;
					field.errorMessage = "Please enter a valid phone number";
					field.valid = false;
					continue;
				}
				field.error = false;
				field.errorMessage = "";
				field.valid = true;
			}
			return this.formValid;
		},
		/**
		 * Validate an email address
		 * @param {string} email
		 */
		validateEmail: function (email) {
			const re = /\S+@\S+\.\S+/;
			return re.test(email);
		},
	},
};
</script>

<style scoped>
@import "./styles.scss";
</style>
